import api from './api'

export type SkillCatalogResult = {
  id: string
  canonicalName: string
  category?: { id: string; name: string } | null
  industries?: Array<{ id: string; name: string }>
  aliases?: Array<{ id: string; name: string }>
  status: string
  matchType: 'exact' | 'alias' | 'fuzzy' | 'suggestion'
  confidence: number
}

export type WorkerCanonicalSkill = {
  id?: string
  tenant_id?: string
  worker_profile_id?: string
  skill_id: string
  source?: 'self_reported' | 'admin_added' | 'verified' | 'mentorship' | 'imported' | string
  level?: string | null
  verification_status?: 'self_reported' | 'pending_verification' | 'verified' | 'rejected' | 'learned_through_mentorship' | string
  canonical_name: string
  skill_status?: string
  category_name?: string | null
}

export type WorkerSkillProgression = {
  id: string
  workerProfileId: string
  skillId: string
  canonicalName: string
  category?: string | null
  currentLevel?: string | null
  currentLevelRank?: number | null
  isCertified?: boolean
  certifiedLevelFloor?: string | null
  certifiedLevelFloorRank?: number | null
  certificationExpiresAt?: string | null
  completedShiftCount?: number | null
  completedVerifiedHours?: number | null
  mentorshipMinutesCompleted?: number | null
  mentorshipMinutesTaught?: number | null
  evidenceScore?: number | null
  evidenceSummary?: Record<string, number>
  lastProgressionRecalculatedAt?: string | null
  progressionMetadata?: Record<string, unknown>
}

export type WorkerSkillEvidence = {
  id: string
  workerProfileId: string
  skillId: string
  canonicalName: string
  evidenceType: string
  status: string
  sourceDocumentId?: string | null
  sourceDocumentName?: string | null
  sourceJobAssignmentId?: string | null
  sourceJobShiftId?: string | null
  sourceMentorshipSessionId?: string | null
  sourceReviewId?: string | null
  occurredAt?: string | null
  expiresAt?: string | null
  scoreDelta?: number | string | null
  levelDelta?: number | string | null
  evidenceWeight?: number | string | null
  metadata?: Record<string, unknown>
  createdAt?: string | null
}

export type SkillSearchResponse = {
  success: boolean
  results: SkillCatalogResult[]
  canRequestNewSkill: boolean
  normalizedQuery: string
}

export type SkillRequestStatus = 'pending' | 'approved' | 'mapped' | 'rejected'

export type PendingSkillRequest = {
  id: string
  tenant_id: string
  requested_name: string
  normalized_key: string
  context?: string | null
  status: SkillRequestStatus
  requested_by?: string | null
  requested_by_email?: string | null
  mapped_skill_id?: string | null
  resolved_by?: string | null
  resolved_at?: string | null
  created_at: string
  suggestions?: SkillCatalogResult[]
}

export type SkillMergePreview = {
  sourceSkill: { id: string; canonical_name: string; status: string; merged_into_skill_id?: string | null }
  targetSkill: { id: string; canonical_name: string; status: string; merged_into_skill_id?: string | null }
  impact: Record<string, number>
}

export type SkillRecommendation = {
  skillId: string
  canonicalName: string
  category?: string | null
  recommendationType: 'related' | 'recommended' | 'required' | 'progression'
  sourceCount: number
  score: number
}

export type WorkerSkillExperience = {
  id: string
  workerProfileId: string
  skillId: string
  canonicalName: string
  category?: string | null
  experienceHours: number
  experienceAssignments: number
  firstUsedAt?: string | null
  lastUsedAt?: string | null
  assignmentHours: number
  mentorshipHours: number
  manualHours: number
  certificationHours: number
  adminAdjustmentHours: number
  sourceCounts?: Record<string, number>
  levelKey: string
  levelLabel: string
}

export type CareerPath = {
  id: string
  name: string
  description?: string | null
  status: string
  steps: Array<{
    id: string
    stepOrder: number
    title: string
    description?: string | null
    targetSkillId?: string | null
    targetSkillName?: string | null
    requiredSkillIds: string[]
    recommendedSkillIds: string[]
  }>
}

export type SkillGapItem = {
  skillId: string
  canonicalName: string
  requirementType: string
}

export type WorkforceDevelopmentAnalytics = {
  topMissingSkills: Array<{ skillId: string; canonicalName: string; count: number }>
  mostRequestedSkills: Array<{ requestedName: string; normalizedKey: string; count: number }>
  fastestGrowingSkills: Array<{ skillId: string; canonicalName: string; count: number }>
  skillsWithNoAvailableMentors: Array<{ skillId: string; canonicalName: string; activeGoalCount: number }>
  skillsBlockingJobFulfillment: Array<{ skillId: string; canonicalName: string; openJobCount: number }>
  mostExperiencedSkills: Array<{ skillId: string; canonicalName: string; experienceHours: number; experienceAssignments: number; workerCount: number }>
  emergingSkills: Array<{ skillId: string; canonicalName: string; workerCount: number; experienceHours: number }>
  skillShortages: Array<{ skillId: string; canonicalName: string; openJobCount: number }>
  workersNearPromotion: Array<{ workerProfileId: string; workerName: string; skillId: string; canonicalName: string; experienceHours: number; nextLevel: string; nextLevelHours: number }>
  potentialMentors: Array<{ workerProfileId: string; workerName: string; skillId: string; canonicalName: string; experienceHours: number; skillLevel: string }>
}

export const skillService = {
  searchSkills: (query: string, params: { industryId?: string; categoryId?: string; includeGlobal?: boolean } = {}) =>
    api.get<SkillSearchResponse>('/api/skills/search', { params: { q: query, ...params } }).then((response) => response.data),

  getSuggestions: (role: string) =>
    api.get<SkillSearchResponse>('/api/skills/suggestions', { params: { role } }).then((response) => response.data),

  requestNewSkill: (requestedName: string, context?: string | null) =>
    api.post('/api/skills/requests', { requestedName, context }).then((response) => response.data),

  listMyWorkerSkills: () =>
    api.get('/api/workers/me/canonical-skills').then((response) => response.data),

  addMyWorkerSkill: (skillId: string, level?: string | null) =>
    api.post('/api/workers/me/canonical-skills', { skillId, level }).then((response) => response.data),

  removeMyWorkerSkill: (skillId: string) =>
    api.delete(`/api/workers/me/canonical-skills/${skillId}`).then((response) => response.data),

  listAdminWorkerSkills: (workerProfileId: string) =>
    api.get<{ success: boolean; skills: WorkerCanonicalSkill[] }>(`/api/admin/workers/${workerProfileId}/canonical-skills`).then((response) => response.data),

  addAdminWorkerSkill: (workerProfileId: string, skillId: string, payload: { level?: string | null; source?: string } = {}) =>
    api.post(`/api/admin/workers/${workerProfileId}/canonical-skills`, { skillId, ...payload }).then((response) => response.data),

  removeAdminWorkerSkill: (workerProfileId: string, skillId: string) =>
    api.delete(`/api/admin/workers/${workerProfileId}/canonical-skills/${skillId}`).then((response) => response.data),

  listSkillRequests: (status?: SkillRequestStatus) =>
    api.get<{ success: boolean; requests: PendingSkillRequest[] }>('/api/admin/skills/requests', { params: status ? { status } : undefined }).then((response) => response.data),

  resolveSkillRequest: (requestId: string, payload: { action: 'approve_new' | 'map_existing' | 'reject'; mappedSkillId?: string | null }) =>
    api.post(`/api/admin/skills/requests/${requestId}/resolve`, payload).then((response) => response.data),

  previewSkillMerge: (sourceSkillId: string, targetSkillId: string) =>
    api.post<{ success: boolean; preview: SkillMergePreview }>('/api/admin/skills/merge/preview', { sourceSkillId, targetSkillId }).then((response) => response.data),

  mergeSkills: (sourceSkillId: string, targetSkillId: string) =>
    api.post('/api/admin/skills/merge', { sourceSkillId, targetSkillId }).then((response) => response.data),

  listMyRecommendedSkills: () =>
    api.get<{ success: boolean; recommendations: SkillRecommendation[] }>('/api/workers/me/recommended-skills').then((response) => response.data),

  listAdminWorkerRecommendations: (workerProfileId: string) =>
    api.get<{ success: boolean; recommendations: SkillRecommendation[] }>(`/api/admin/workers/${workerProfileId}/recommended-skills`).then((response) => response.data),

  listMySkillExperience: () =>
    api.get<{ success: boolean; experience: WorkerSkillExperience[] }>('/api/workers/me/skill-experience').then((response) => response.data),

  getMyProgression: () =>
    api.get<{ success: boolean; progression: { experience: WorkerSkillExperience[]; recommendations: SkillRecommendation[]; nextLevels: Array<{ skillId: string; canonicalName: string; currentLevel: string; experienceHours: number }> } }>('/api/workers/me/progression').then((response) => response.data),

  listAdminWorkerSkillExperience: (workerProfileId: string) =>
    api.get<{ success: boolean; experience: WorkerSkillExperience[] }>(`/api/admin/workers/${workerProfileId}/skill-experience`).then((response) => response.data),

  listAdminWorkerSkillProgression: (workerProfileId: string) =>
    api.get<{ success: boolean; progression: WorkerSkillProgression[] }>(`/api/admin/workers/${workerProfileId}/skill-progression`).then((response) => response.data),

  listAdminWorkerSkillEvidence: (workerProfileId: string, skillId?: string | null) =>
    api.get<{ success: boolean; evidence: WorkerSkillEvidence[] }>(`/api/admin/workers/${workerProfileId}/skill-evidence`, { params: skillId ? { skillId } : undefined }).then((response) => response.data),

  listCareerPaths: (skillId?: string | null) =>
    api.get<{ success: boolean; careerPaths: CareerPath[] }>('/api/career-paths', { params: skillId ? { skillId } : undefined }).then((response) => response.data),

  getJobSkillGap: (jobId: string, workerProfileId: string) =>
    api.get<{ success: boolean; matchedSkills: SkillGapItem[]; missingSkills: SkillGapItem[] }>(`/api/jobs/${jobId}/skill-gap`, { params: { workerProfileId } }).then((response) => response.data),

  listMentorshipRecommendations: (skillId: string, workerProfileId?: string) =>
    api.get<{ success: boolean; recommendedSkills: SkillRecommendation[]; mentors: Array<{ mentorWorkerProfileId: string; mentorName: string; coveredGapCount: number; coveredSkills: string[] }> }>(
      workerProfileId ? '/api/admin/mentorship/recommendations' : '/api/workers/me/mentorship/recommendations',
      { params: workerProfileId ? { skillId, workerProfileId } : { skillId } }
    ).then((response) => response.data),

  getWorkforceDevelopmentAnalytics: () =>
    api.get<{ success: boolean; analytics: WorkforceDevelopmentAnalytics }>('/api/admin/workforce-development').then((response) => response.data),
}
